import {useViewportFitHeight} from '@github-ui/docked-side-panel/useViewportFitHeight'
import {ArtifactsPanel, useEmbeddedAvailable} from '@github-ui/scribe-artifacts/ArtifactPanel'
import {useArtifactsPanelState} from '@github-ui/scribe-artifacts/ArtifactPanelContext'
import {SplitPageLayout} from '@primer/react'
import {clsx} from 'clsx'
import {type ReactNode, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import styles from './ArtifactsPanelLayout.module.css'

const SIDEBAR_WIDTH = {
  min: '450px',
  default: '544px',
  max: '768px',
} as const

function usePageFooter(enabled: boolean) {
  const subscribe = useCallback(
    (notify: () => void) => {
      if (!enabled) return () => {}
      const observer = new MutationObserver(notify)
      observer.observe(document.body, {childList: true, subtree: true})
      return () => observer.disconnect()
    },
    [enabled],
  )
  const getSnapshot = useCallback(
    () => (enabled ? document.querySelector<HTMLElement>('footer.footer') : null),
    [enabled],
  )
  return useSyncExternalStore(subscribe, getSnapshot, () => null)
}

export function ArtifactsPanelLayout({
  children,
  insideSidePanel = false,
}: {
  children: ReactNode
  insideSidePanel?: boolean
}) {
  const {activeArtifactId} = useArtifactsPanelState()
  const embeddedAvailable = useEmbeddedAvailable()
  const panelOpen = activeArtifactId !== null
  const panelOpenDocked = panelOpen && embeddedAvailable && !insideSidePanel
  const [hasOpenedDocked, setHasOpenedDocked] = useState(false)
  const panelContentRef = useRef<HTMLDivElement>(null)
  const activeDockedTabRef = useRef<HTMLButtonElement>(null)
  const previousEmbeddedAvailableRef = useRef(embeddedAvailable)
  const footer = usePageFooter(panelOpenDocked)
  const footerRef = useMemo(() => ({current: footer}), [footer])

  if (panelOpenDocked && !hasOpenedDocked) {
    setHasOpenedDocked(true)
  }

  useEffect(() => {
    const wasEmbeddedAvailable = previousEmbeddedAvailableRef.current
    previousEmbeddedAvailableRef.current = embeddedAvailable
    if (wasEmbeddedAvailable || !panelOpenDocked) return

    activeDockedTabRef.current?.focus()
  }, [embeddedAvailable, panelOpenDocked])

  useViewportFitHeight(panelContentRef, {
    enabled: panelOpenDocked,
    bottomBoundaryRef: footerRef,
    clampStickyAncestor: true,
  })

  if (insideSidePanel) {
    return (
      <>
        {children}
        <ArtifactsPanel mode="overlay" />
      </>
    )
  }

  return (
    <>
      <div
        className={styles.Layout}
        data-artifacts-panel-open={panelOpenDocked}
        data-testid={TEST_IDS.issueArtifactsPanelLayout}
      >
        <SplitPageLayout>
          <SplitPageLayout.Content as="div" width="full" padding="none" className={styles.Content}>
            <div className={styles.IssueContent}>{children}</div>
          </SplitPageLayout.Content>
          <SplitPageLayout.Sidebar
            resizable={panelOpenDocked}
            className={clsx(styles.SidePanel, !panelOpenDocked && styles.HiddenSidePanel)}
            divider="none"
            padding="none"
            sticky
            position="end"
            width={SIDEBAR_WIDTH}
          >
            <div ref={panelContentRef} className="height-full">
              {embeddedAvailable && hasOpenedDocked && (
                <ArtifactsPanel mode="docked" activeTabRef={activeDockedTabRef} />
              )}
            </div>
          </SplitPageLayout.Sidebar>
        </SplitPageLayout>
      </div>
      {!embeddedAvailable && <ArtifactsPanel mode="overlay" />}
    </>
  )
}
